import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { firstValueFrom } from 'rxjs';

import { PartnerImportOption, PartnerImportResult } from 'src/app/models/partner-played.model';
import { TerpecaRoom } from 'src/app/models/room.model';
import { AccountIdFieldInfo, TerpecaUser, getTrackerPartner } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { PartnerImportService } from 'src/app/services/partner-import.service';
import { ACCOUNT_ID_VALIDATORS } from 'src/app/utils/form.utils';
import { ImportReview, buildImportReview, checkedRoomIds, sortImportOptions } from 'src/app/utils/partner-import.utils';
import { SimpleDialogComponent } from 'src/app/views/simpledialog/simpledialog.component';

/** What the dialog needs from the voting page (RankComponent). */
export interface PartnerImportTarget {
  user: TerpecaUser;
  rankableRoomMap: Map<string, TerpecaRoom>;
  roomCountrySort: (a: TerpecaRoom, b: TerpecaRoom) => number;
  isRoomPlayed: (roomId: string) => boolean;
  isAffiliatedRoom: (roomId: string) => boolean;
  addImportedRooms: (roomIds: string[]) => Promise<void>;
}

export interface PartnerImportDialogData {
  target: PartnerImportTarget;
  options: PartnerImportOption[];
  /** The site to start with, when the voter came from that site's line on the page. */
  partnerId?: string;
}

// Imports the voter's played rooms from one tracking site, then lets them review what to add. Opened from the
// select steps of the voting flow.
@Component({
  selector: 'app-partnerimportdialog',
  standalone: false,
  templateUrl: './partnerimportdialog.component.html',
  styleUrl: './partnerimportdialog.component.css'
})
export class PartnerImportDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<PartnerImportDialogComponent>>(MatDialogRef);
  data = inject<PartnerImportDialogData>(MAT_DIALOG_DATA);
  private dialog = inject(MatDialog);
  private auth = inject(AuthService);
  private importService = inject(PartnerImportService);

  options: PartnerImportOption[] = [];
  siteControl = new UntypedFormControl(null, Validators.required);
  idControl = new UntypedFormControl('', Validators.required);
  loading = false;
  adding = false;
  result: PartnerImportResult | null = null;
  error = '';
  review: ImportReview | null = null;

  ngOnInit() {
    // Leaving the review (Escape or clicking outside, like Back) would throw away what the voter has checked.
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(async () => {
      await this.requestClose();
    });
    this.dialogRef.keydownEvents().subscribe(async event => {
      if (event.key === 'Escape') {
        await this.requestClose();
      }
    });
    this.options = sortImportOptions(this.data.options);
    this.siteControl.valueChanges.subscribe(() => {
      this.siteChanged();
    });
    const start = this.options.find(option => option.partnerId === this.data.partnerId);
    if (start) {
      this.siteControl.setValue(start);
    }
  }

  get site(): PartnerImportOption | null {
    return this.siteControl.value;
  }

  /** The tracking site's profile field; unset for sandbox mock partners. */
  get partner(): AccountIdFieldInfo | undefined {
    return this.site && !this.site.mock ? getTrackerPartner(this.site.partnerId) : undefined;
  }

  canImport(option: PartnerImportOption | null): boolean {
    return option?.status === 'live';
  }

  private siteChanged() {
    const partner = this.partner;
    this.idControl.setValidators([Validators.required, ...(partner ? [ACCOUNT_ID_VALIDATORS[partner.field]] : [])]);
    this.idControl.setValue(partner ? String(this.data.target.user[partner.field] || '') : '');
    this.result = null;
    this.error = '';
  }

  async importRooms() {
    const site = this.site;
    if (!site || !this.canImport(site) || this.idControl.invalid) {
      this.idControl.markAsTouched();
      return;
    }
    const partner = this.partner;
    const id = String(this.idControl.value).trim();
    this.loading = true;
    this.result = null;
    this.error = '';
    try {
      if (partner && id !== String(this.data.target.user[partner.field] || '')) {
        await this.auth.updateUser({ [partner.field]: id });
      }
      const result = await this.importService.importRooms(site.partnerId, partner ? undefined : id);
      const target = this.data.target;
      const review = result.outcome === 'ok' ?
        buildImportReview([{ label: site.label, result }], target.rankableRoomMap, target.roomCountrySort,
                          roomId => target.isRoomPlayed(roomId), roomId => target.isAffiliatedRoom(roomId)) : null;
      Object.assign(this, { result, review });
    } catch (error: unknown) {
      console.error(`partner import from ${site.partnerId} failed`, error);
      this.error = 'Something went wrong. Please try again later.';
    } finally {
      this.loading = false;
    }
  }

  checkedCount(): number {
    const target = this.data.target;
    return this.review ? checkedRoomIds(this.review, target.rankableRoomMap, roomId => target.isRoomPlayed(roomId),
                                        roomId => target.isAffiliatedRoom(roomId)).length : 0;
  }

  hasRoomsToReview(): boolean {
    return Boolean(this.review && (this.review.played.length || this.review.maybeGroups.length));
  }

  private backToSite() {
    this.review = null;
    this.result = null;
  }

  private async requestClose() {
    if (this.adding) {
      return;
    }
    if (this.review) {
      await this.leaveReview(true);
    } else {
      this.dialogRef.close(false);
    }
  }

  // Back (or closing) from the review: if rooms are checked but not added yet, ask whether to add them first.
  // Dismissing the question stays in the review, so nothing is lost by accident.
  async leaveReview(thenClose: boolean) {
    const count = this.checkedCount();
    if (count) {
      const plural = count === 1 ? '' : 's';
      const choice: unknown = await firstValueFrom(this.dialog.open(SimpleDialogComponent, {
        data: {
          message: `You've checked ${count} room${plural} that ${count === 1 ? 'hasn\'t' : 'haven\'t'} been added yet.`,
          okLabel: `Add ${count} room${plural} and ${thenClose ? 'close' : 'go back'}`,
          cancelLabel: count === 1 ? 'Discard it' : 'Discard them'
        }
      }).afterClosed());
      if (typeof choice !== 'boolean') {
        return;
      }
      if (choice) {
        await this.addChecked(thenClose);
        return;
      }
    }
    if (thenClose) {
      this.dialogRef.close(false);
    } else {
      this.backToSite();
    }
  }

  async addChecked(closeAfter = true) {
    if (!this.review) {
      return;
    }
    const target = this.data.target;
    this.adding = true;
    try {
      await target.addImportedRooms(checkedRoomIds(this.review, target.rankableRoomMap,
                                                   roomId => target.isRoomPlayed(roomId),
                                                   roomId => target.isAffiliatedRoom(roomId)));
      if (closeAfter) {
        this.dialogRef.close(true);
      } else {
        this.backToSite();
      }
    } finally {
      this.adding = false;
    }
  }

  close() {
    this.dialogRef.close(false);
  }
}
